import { type ReactNode, useCallback, useEffect, useMemo } from 'react';
import { DrawerLayout } from 'components/DrawerLayout/DrawerLayout';
import { useResponsiveDrawerColumns } from 'hooks/useResponsiveDrawerColumns';
import { debounce, isArray } from 'lodash';
import { fieldMappingSelector, fieldsLoading } from 'slices/field.slice';
import { useAppSelector } from 'slices/store';
import { isFieldSaveable } from 'utils/fieldUtils';
import { EFieldType } from '@timeedit/types';
import type { TTEFieldValue } from '@timeedit/types/lib/types';
import { Icon, TEDrawerSection, TEInput } from '@timeedit/ui-components';
import {
  Button,
  Form,
  Input,
  type SelectProps,
  Skeleton,
  Space,
} from '@timeedit/ui-components/antd';
import type { ObjectManagerDetailProps } from '../ObjectManagerDetail';
import { getIntegerRangeErrorMessage } from '../components/bulkEdit/common';

const Fields = ({ object, onChange }: ObjectManagerDetailProps) => {
  const [form] = Form.useForm();
  const { formLayout } = useResponsiveDrawerColumns();
  const fieldMap = useAppSelector(fieldMappingSelector);
  const fieldsIsLoading = useAppSelector(fieldsLoading);

  const getSelectProps: (
    editable: boolean,
    multiple: boolean
  ) => Partial<Pick<SelectProps, 'maxCount' | 'mode'>> = (
    editable,
    multiple
  ) => {
    if (!editable && !multiple) {
      return {};
    }
    if (editable && !multiple) {
      return {
        maxCount: 1,
        mode: 'tags',
      };
    }
    if (!editable && multiple) {
      return {
        mode: 'multiple',
      };
    }
    return {
      mode: 'tags',
    };
  };

  const getInput = useCallback(
    (fieldId: number) => {
      const field = fieldMap[fieldId];
      if (!field) return null;
      const fieldName = field.name?.replace(/\s+/g, '-');
      const commonProps = {
        'data-testid': `field-${fieldName}`,
      };
      const isMultipleValue = field.multiple;
      const listKey = `${field.id}`;
      const defaultRules = [
        {
          required: field.mandatory,
          message: `${field.name} is required!`,
        },
        {
          validator: (_: unknown, value: string | number) => {
            if (!field.filter) {
              return Promise.resolve();
            }
            try {
              const regex = new RegExp(field.filter);
              const stringValue = String(value ?? '');
              if (stringValue && !regex.test(stringValue)) {
                return Promise.reject(
                  new Error(
                    `${field.name} does not match the pattern ${field.filter}`
                  )
                );
              }
              return Promise.resolve();
            } catch {
              return Promise.reject(
                new Error(
                  `${field.name} contains an invalid regex (${field.filter}), please check the field settings`
                )
              );
            }
          },
        },
      ];
      const listRules = field.mandatory
        ? [
            {
              validator: (_: unknown, value: (string | number)[]) => {
                if (!value || value.length === 0) {
                  return Promise.reject(
                    new Error(`${field.name} is required!`)
                  );
                }
                return Promise.resolve();
              },
            },
          ]
        : [];

      // Shared wrapper for single-value fields
      const renderSingleField = (
        rules: Record<string, unknown>[],
        input: ReactNode
      ) => (
        <Form.Item
          style={{ paddingBottom: '4px' }}
          key={field.id}
          label={`${field.name}`}
          tooltip={field.extId}
          name={field.id}
          rules={rules}
        >
          {input}
        </Form.Item>
      );

      // Shared wrapper for multiple-value fields (Form.List)
      const renderMultipleField = (
        itemRules: Record<string, unknown>[],
        renderInput: () => ReactNode,
        defaultValue: unknown = ''
      ) => (
        <Form.Item
          key={field.id}
          label={`${field.name}`}
          tooltip={field.extId}
          required={field.mandatory}
          style={{ paddingBottom: '4px', marginBottom: 0 }}
        >
          <Form.List key={listKey} name={`${field.id}`} rules={listRules}>
            {(fields, { add, remove }, { errors }) => (
              <Space direction="vertical" style={{ width: '100%' }} size={4}>
                {fields.map(({ key, name, ...restField }) => (
                  <div
                    key={key}
                    style={{
                      display: 'flex',
                      alignItems: 'flex-start',
                      gap: 8,
                    }}
                  >
                    <Form.Item
                      name={name}
                      {...restField}
                      style={{ marginBottom: 0, flex: 1, minWidth: 0 }}
                      rules={[
                        {
                          validator: (_: unknown, value: unknown) => {
                            if (value === '' || value == null) {
                              return Promise.reject(
                                new Error(`${field.name} is required!`)
                              );
                            }
                            return Promise.resolve();
                          },
                        },
                        ...itemRules.filter((rule) => !('required' in rule)),
                      ]}
                    >
                      {renderInput()}
                    </Form.Item>
                    <Button
                      type="text"
                      size="small"
                      style={{ border: '1px solid #00000073' }}
                      icon={<Icon id="DeleteOutlined" alt="Delete" />}
                      onClick={() => remove(name)}
                    />
                  </div>
                ))}
                <Button
                  size="small"
                  type="dashed"
                  onClick={() => add(defaultValue)}
                >
                  Add
                </Button>
                <Form.ErrorList errors={errors} />
              </Space>
            )}
          </Form.List>
        </Form.Item>
      );

      switch (field.fieldType) {
        case EFieldType.CATEGORY:
          return renderSingleField(
            [
              {
                required: field.mandatory,
                message: `${field.name} is required!`,
              },
              {
                validator: (_, value) => {
                  if (value) {
                    if (value.length > 500) {
                      // Custom unconfigurable rule from TE Server, maximum of 500 items
                      return Promise.reject(
                        new Error(`${field.name} must be 500 or less items!`)
                      );
                    }

                    const isValid = isArray(value)
                      ? value.every(
                          (item: string) => item.length <= field.length
                        )
                      : value.length <= field.length;

                    if (!isValid) {
                      // Each item must be less than the length of the field
                      return Promise.reject(
                        new Error(
                          `All values in ${field.name} must be ${field.length} characters or less`
                        )
                      );
                    }
                    return Promise.resolve();
                  }
                },
              },
            ],
            <TEInput.Select
              {...commonProps}
              {...getSelectProps(field.editable, field.multiple)}
              placeholder="Select"
              options={field.categories.map((value) => ({
                label: value,
                value,
              }))}
            />
          );

        case EFieldType.CHECKBOX:
          return renderSingleField(
            [
              {
                required: field.mandatory,
                message: `${field.name} is required!`,
              },
            ],
            <TEInput.Switch {...commonProps} />
          );

        case EFieldType.COMMENT:
          return renderSingleField(
            [{ max: field.length }],
            <TEInput.TextArea
              defaultValue=""
              maxLength={field.length}
              {...commonProps}
            />
          );

        case EFieldType.INTEGER:
        case EFieldType.LENGTH: {
          const numberRules = [
            {
              required: field.mandatory,
              message: `${field.name} is required!`,
            },
            {
              validator: (_: unknown, value: unknown) => {
                if (value == null) return Promise.resolve();
                const num = Number(value);
                if (
                  !Number.isInteger(num) ||
                  (field.min != null && num < field.min) ||
                  (field.max != null && num > field.max)
                ) {
                  return Promise.reject(
                    new Error(getIntegerRangeErrorMessage(field.min, field.max))
                  );
                }
                return Promise.resolve();
              },
            },
          ];
          const isDisabled =
            field.fieldType === EFieldType.INTEGER &&
            Number(field.sumType) !== 0;
          const numberInput = () => (
            <TEInput.InputNumber
              style={{ width: '100%' }}
              disabled={isDisabled}
              min={field.min}
              max={field.max}
              {...commonProps}
            />
          );
          if (isMultipleValue) {
            return renderMultipleField(
              numberRules,
              numberInput,
              field.min ?? 1
            );
          }
          return renderSingleField(numberRules, numberInput());
        }

        default: {
          const textInput = () => <Input {...commonProps} />;
          if (isMultipleValue) {
            return renderMultipleField(defaultRules, textInput);
          }
          return renderSingleField(defaultRules, textInput());
        }
      }
    },
    [fieldMap, object?.fields]
  );

  useEffect(() => {
    if (!object?.fields) {
      form.setFieldsValue({});
    }
    const constructedForm = object.fields.reduce((acc, field) => {
      const fieldDetails = fieldMap[field.fieldId];
      if (fieldDetails === undefined || !isFieldSaveable(fieldDetails))
        return acc;

      switch (fieldDetails.fieldType) {
        case EFieldType.CHECKBOX:
          if (field.values.length === 0) {
            return { ...acc, [field.fieldId]: false };
          }
          if (field.values.length === 1) {
            return {
              ...acc,
              [field.fieldId]: field.values[0] === '1' ? true : false,
            };
          }
          return {
            ...acc,
            [field.fieldId]: field.values.map((value) => value === '1'),
          };
        case EFieldType.CATEGORY:
          return { ...acc, [field.fieldId]: field.values };
        case EFieldType.INTEGER:
        case EFieldType.LENGTH:
          if (fieldDetails.multiple) {
            return {
              ...acc,
              [field.fieldId]: field.values.map(Number),
            };
          }
          return { ...acc, [field.fieldId]: Number(field.values[0]) || 0 };
        default:
          if (fieldDetails.multiple) {
            return { ...acc, [field.fieldId]: field.values };
          }
          return { ...acc, [field.fieldId]: field.values[0] || '' };
      }
    }, {});
    form.setFieldsValue(constructedForm);
  }, [fieldMap, object?.fields]);

  const onChangeDebounce = debounce((changedValue, fullForm) => {
    const fields = Object.entries(fullForm).map(([key, value]) => {
      const fieldId = Number(key);

      const translateBoolean = (value: boolean) => {
        if (value) return '1';
        return '0';
      };

      // Handle empty values, gotcha that value=0 was evaluated as false
      if (!value && isNaN(Number(value))) {
        return {
          fieldId,
          values: [],
        };
      }

      if (typeof value === 'boolean') {
        return {
          fieldId,
          values: [translateBoolean(value)],
        };
      }

      if (isArray(value)) {
        return {
          fieldId,
          values: value,
        };
      }

      if (value === '') {
        return {
          fieldId,
          values: [],
        };
      }

      return {
        fieldId,
        values: [value],
      };
    });

    // Sort fields in the same order as the original object TODO pointless now?
    fields.sort((a, b) => {
      const fieldAIndex = object.fields.findIndex(
        (field) => field.fieldId === a.fieldId
      );
      const fieldBIndex = object.fields.findIndex(
        (field) => field.fieldId === b.fieldId
      );

      if (fieldAIndex !== -1 && fieldBIndex !== -1) {
        return fieldAIndex - fieldBIndex;
      }
      return 0;
    });

    onChange({ fields });
  }, 300);

  useEffect(() => {
    return () => {
      onChangeDebounce.cancel();
    };
  }, [onChangeDebounce]);

  const formItems: ReactNode[] = useMemo(() => {
    if (!object || !object.fields || object.fields.length === 0) {
      return [];
    }
    return object.fields
      .filter((fieldIdValueTuple: TTEFieldValue) =>
        isFieldSaveable(fieldMap[fieldIdValueTuple.fieldId])
      )
      .map((fieldIdValueTuple: TTEFieldValue) =>
        getInput(fieldIdValueTuple.fieldId)
      );
  }, [object, fieldMap]);

  return (
    <TEDrawerSection label="Fields">
      {fieldsIsLoading ? (
        <DrawerLayout>
          <Space direction="horizontal" size={8}>
            <Skeleton.Input active={true} />
            <Skeleton.Input active={true} />
          </Space>
          <Space direction="horizontal" size={8}>
            <Skeleton.Input active={true} />
            <Skeleton.Input active={true} />
          </Space>
          <Space direction="horizontal" size={8}>
            <Skeleton.Input active={true} />
            <Skeleton.Input active={true} />
          </Space>
        </DrawerLayout>
      ) : (
        <Form
          form={form}
          {...formLayout}
          labelAlign="left"
          onValuesChange={onChangeDebounce}
          validateTrigger={['onBlur', 'onChange']}
        >
          <DrawerLayout>{formItems}</DrawerLayout>
        </Form>
      )}
    </TEDrawerSection>
  );
};

export default Fields;
